import { useMemo } from "react";
import useCurrentUser from "./useCurrentUser";
import {
    extractPermissions,
    hasPermission,
    hasAnyPermission,
    hasFullAccess,
    isShopOwner,
} from "../lib/permissions/permissions";

/**
 * Hook pour gérer les permissions de l'utilisateur connecté.
 *
 * Utilisation :
 *   const { can, canAny, permissions, isLoading } = usePermissions();
 *   if (can("products:create")) { ... }
 *   {can("products:delete") && <button>Supprimer</button>}
 */
const usePermissions = () => {
    const { data: user, isLoading } = useCurrentUser();

    return useMemo(() => {
        const permissions = extractPermissions(user);

        return {
            user,
            permissions,
            isLoading,
            isOwner: isShopOwner(user),
            fullAccess: hasFullAccess(user),

            /** L'utilisateur possède la (ou les) permission(s) requise(s). */
            can: (required) => hasPermission(user, required),

            /** L'utilisateur possède au moins une des permissions listées. */
            canAny: (requiredList) => hasAnyPermission(user, requiredList),

            /** Toutes les permissions listées sont possédées. */
            canAll: (requiredList) => hasPermission(user, requiredList),
        };
    }, [user, isLoading]);
};

export default usePermissions;
